import { useRef, useState, type ReactNode } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { Ellipsis } from 'lucide-react';
import { ErrorCode } from '@brimo/shared';
import { clsx } from '../../../ds/clsx';
import {
  Banner,
  Button,
  EmptyState,
  Icon,
  MenuItem,
  MenuList,
  Popover,
  Skeleton,
  Tooltip,
  useToast,
} from '../../../ds/components';
import { ApiError, errorMessage } from '../../../lib/api';
import { usePermissions } from '../../../lib/session';
import { LockedPage } from '../../../shell/ModulePages';
import { CreateTaskDialog } from '../../tasks/CreateTaskDialog';
import { useFinancing, useInvalidateFinancings } from '../api';
import { LinkDealDialog } from '../dialogs/LinkDealDialog';
import { LoseDialog } from '../dialogs/LoseDialog';
import { ReassignDialog } from '../dialogs/ReassignDialog';
import { ReopenDialog } from '../dialogs/ReopenDialog';
import { StageChangeDialog } from '../dialogs/StageChangeDialog';
import { DocumentsTab } from '../financier/DocumentsTab';
import { FinancierHeaderActions } from '../financier/FinancierHeaderActions';
import { FinancierOverview } from '../financier/FinancierOverview';
import { NotAssignedPage } from '../financier/NotAssignedPage';
import { OffersTab } from '../financier/OffersTab';
import { SignatureDialog } from '../financier/SignatureDialog';
import type { Action, FinancingRecord } from '../types';
import { HistoryMain } from './HistoryTab';
import { OverviewMain } from './OverviewTab';
import { RecordFrame, isRecordTab, type RecordTab } from './RecordFrame';
import { SelfDisclosureMain } from './SelfDisclosureTab';
import { TeamSidebar } from './Sidebar';
import styles from './record.module.css';

type DialogKind = 'stage' | 'lose' | 'reopen' | 'link' | 'reassign' | 'signature' | 'task';

function RecordSkeleton() {
  return (
    <div className={styles.page} aria-busy="true">
      <div className={styles.top}>
        <Skeleton width={160} height={12} />
        <Skeleton width={420} height={30} />
        <Skeleton width={360} height={12} />
        <Skeleton variant="block" height={30} />
      </div>
      <div className={styles.content}>
        <div className={styles.main}>
          <Skeleton variant="block" height={180} />
          <Skeleton variant="block" height={220} />
        </div>
        <div className={styles.side}>
          <Skeleton variant="block" height={220} />
          <Skeleton variant="block" height={160} />
        </div>
      </div>
    </div>
  );
}

/** A header button that shows the server's reason on hover/focus while it is not allowed (as DA-05 „nur M. Kessler"). */
function ActionButton({
  action,
  variant = 'secondary',
  onClick,
  label,
}: {
  action: Action;
  variant?: 'primary' | 'secondary';
  onClick: () => void;
  label: string;
}) {
  const button = (
    <Button variant={variant} disabled={!action.allowed} onClick={onClick}>
      {label}
    </Button>
  );
  if (action.allowed || !action.reason) return button;
  return (
    <Tooltip content={action.reason}>
      <span className={styles.disabledAnchor} tabIndex={0} aria-label={`${label}: ${action.reason}`}>
        {button}
      </span>
    </Tooltip>
  );
}

interface MenuEntry {
  key: string;
  label: string;
  tone?: 'danger' | undefined;
  onSelect: () => void;
}

/** „Weitere Aktionen" of the header (Verloren setzen, Neu zuweisen, Mit Deal verknüpfen, Stufe wechseln …). */
function OverflowMenu({ entries }: { entries: MenuEntry[] }) {
  const anchorRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  if (entries.length === 0) return null;
  return (
    <>
      <button
        ref={anchorRef}
        type="button"
        className={clsx('pressable', styles.moreButton)}
        aria-label="Weitere Aktionen"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        <Icon icon={Ellipsis} size={16} />
      </button>
      <Popover
        open={open}
        onClose={() => setOpen(false)}
        anchorRef={anchorRef}
        width={238}
        placement="bottom-end"
        variant="card"
        initialFocus="none"
      >
        <MenuList label="Weitere Aktionen" density="compact" onClose={() => setOpen(false)}>
          {entries.map((entry) => (
            <MenuItem
              key={entry.key}
              label={entry.label}
              tone={entry.tone}
              onSelect={() => {
                setOpen(false);
                entry.onSelect();
              }}
            />
          ))}
        </MenuList>
      </Popover>
    </>
  );
}

/**
 * `/financing/:id` — the financing record (Finanzierungen 06/07/08 for the team, Finanzierer 03–11 for the assigned
 * financier): the frame with the header actions, the tabs `?tab=overview|documents|offers|self-disclosure|history`,
 * the dialogs (Stufe wechseln, Verloren setzen, Wieder öffnen, Mit Deal verknüpfen, Neu zuweisen, Task anlegen).
 * 403 `FINANCING_NOT_ASSIGNED` shows Finanzierer 09.
 */
export function FinancingRecordPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const permissions = usePermissions();
  const { toast } = useToast();
  const invalidate = useInvalidateFinancings();
  const [search, setSearch] = useSearchParams();
  const record = useFinancing(id);
  const [dialog, setDialog] = useState<DialogKind | null>(null);

  const tabParam = search.get('tab');
  const tab: RecordTab = isRecordTab(tabParam) ? tabParam : 'overview';
  function setTab(next: string) {
    if (!isRecordTab(next)) return;
    setSearch(
      (current) => {
        const params = new URLSearchParams(current);
        if (next === 'overview') params.delete('tab');
        else params.set('tab', next);
        return params;
      },
      { replace: true },
    );
  }

  if (permissions.tool('financing') !== 'ok') return <LockedPage tool="financing" />;
  if (record.isPending) return <RecordSkeleton />;
  if (record.isError) {
    const error = record.error;
    if (error instanceof ApiError && error.code === ErrorCode.FINANCING_NOT_ASSIGNED)
      return <NotAssignedPage helper={error.detailString('helper')} />;
    if (error instanceof ApiError && error.status === 403) return <LockedPage tool="financing" />;
    return (
      <div className={styles.page}>
        <div className={clsx(styles.content, styles.contentSingle)}>
          {error instanceof ApiError && error.status === 404 ? (
            <EmptyState
              eyebrow="Finanzierungen"
              title="Diese Finanzierung ist für Sie nicht sichtbar."
              description={error.message}
              actions={
                <Button variant="secondary" onClick={() => void navigate('/financing')}>
                  Zur Liste
                </Button>
              }
            />
          ) : (
            <Banner
              tone="danger"
              actions={
                <Button
                  variant="destructiveFilled"
                  size="small"
                  loading={record.isFetching}
                  onClick={() => void record.refetch()}
                >
                  Erneut laden
                </Button>
              }
            >
              Die Finanzierung konnte nicht geladen werden
              {error instanceof ApiError ? ` · Fehler ${error.status}` : ''}. {errorMessage(error)}
            </Banner>
          )}
        </div>
      </div>
    );
  }

  const financing = record.data;
  const financier = financing.viewer === 'financier';
  const lost = financing.status === 'lost';
  const disbursed = financing.stage.key === 'disbursed';
  const { actions } = financing;
  const onChanged = () => void invalidate(financing.id);

  function done(message: string) {
    return (updated: FinancingRecord) => {
      setDialog(null);
      void invalidate(updated.id);
      toast({ message });
    };
  }

  const menu: MenuEntry[] = [];
  if (financier && actions.changeStage.allowed && !lost)
    menu.push({ key: 'stage', label: 'Stufe wechseln …', onSelect: () => setDialog('stage') });
  if (actions.linkDeal.allowed && financing.linked.deal === null && (financier || lost || disbursed))
    menu.push({ key: 'link', label: 'Mit Deal verknüpfen …', onSelect: () => setDialog('link') });
  if (actions.reassign.allowed && !lost)
    menu.push({
      key: 'reassign',
      label: financing.financier ? 'Neu zuweisen …' : 'Finanzierer zuweisen …',
      onSelect: () => setDialog('reassign'),
    });
  if (actions.reopen.allowed && lost && !financier)
    menu.push({ key: 'reopen', label: 'Wieder öffnen …', onSelect: () => setDialog('reopen') });
  if (actions.lose.allowed && !lost)
    menu.push({ key: 'lose', label: 'Verloren setzen …', tone: 'danger', onSelect: () => setDialog('lose') });

  let headerActions: ReactNode;
  if (financier) {
    headerActions = (
      <>
        <FinancierHeaderActions financing={financing} onChanged={onChanged} tab={tab} onTab={setTab} />
        <OverflowMenu entries={menu} />
      </>
    );
  } else if (lost) {
    headerActions = (
      <>
        {actions.reopen.allowed ? (
          <Button variant="secondary" onClick={() => setDialog('reopen')}>
            Wieder öffnen
          </Button>
        ) : null}
        <OverflowMenu entries={menu.filter((entry) => entry.key !== 'reopen')} />
      </>
    );
  } else if (disbursed) {
    headerActions = (
      <>
        <Button variant="secondary" onClick={() => setTab('documents')}>
          Dokumente herunterladen
        </Button>
        <OverflowMenu entries={menu.filter((entry) => entry.key !== 'link')} />
      </>
    );
  } else {
    headerActions = (
      <>
        <Button variant="secondary" onClick={() => setDialog('stage')}>
          Stufe wechseln
        </Button>
        {financing.linked.deal === null && actions.linkDeal.allowed ? (
          <Button variant="secondary" onClick={() => setDialog('link')}>
            Mit Deal verknüpfen
          </Button>
        ) : null}
        {financing.stage.key === 'commitment' ? (
          <ActionButton
            variant="primary"
            action={actions.recordSignature}
            onClick={() => setDialog('signature')}
            label="Fina-Unterschrift erfassen"
          />
        ) : null}
        <OverflowMenu entries={menu} />
      </>
    );
  }

  const linkDeal =
    actions.linkDeal.allowed && financing.linked.deal === null ? () => setDialog('link') : null;
  const withSidebar = (main: ReactNode) =>
    financier ? (
      <div className={clsx(styles.content, styles.contentSingle)}>
        <div className={styles.main}>{main}</div>
      </div>
    ) : (
      <div className={styles.content}>
        <div className={styles.main}>{main}</div>
        <TeamSidebar
          financing={financing}
          onChanged={onChanged}
          onLinkDeal={linkDeal}
          onCreateTask={() => setDialog('task')}
        />
      </div>
    );

  let content: ReactNode;
  switch (tab) {
    case 'overview':
      content = financier ? (
        <FinancierOverview financing={financing} onChanged={onChanged} />
      ) : (
        <div className={styles.content}>
          <OverviewMain financing={financing} />
          <TeamSidebar
            financing={financing}
            onChanged={onChanged}
            onLinkDeal={linkDeal}
            onCreateTask={() => setDialog('task')}
          />
        </div>
      );
      break;
    case 'documents':
      content = <DocumentsTab financing={financing} onChanged={onChanged} />;
      break;
    case 'offers':
      content = <OffersTab financing={financing} onChanged={onChanged} />;
      break;
    case 'self-disclosure':
      content = withSidebar(<SelfDisclosureMain financing={financing} />);
      break;
    case 'history':
      content = withSidebar(<HistoryMain financingId={financing.id} />);
      break;
  }

  return (
    <>
      <RecordFrame financing={financing} tab={tab} onTab={setTab} actions={headerActions}>
        {content}
      </RecordFrame>
      {dialog === 'stage' ? (
        <StageChangeDialog
          open
          financing={financing}
          onClose={() => setDialog(null)}
          onDone={(updated) => done(`Stufe gesetzt: ${updated.stage.label}.`)(updated)}
        />
      ) : null}
      {dialog === 'lose' ? (
        <LoseDialog
          open
          financingId={financing.id}
          numberLabel={financing.numberLabel}
          onClose={() => setDialog(null)}
          onDone={done('Finanzierung auf verloren gesetzt.')}
        />
      ) : null}
      {dialog === 'reopen' ? (
        <ReopenDialog
          open
          financing={financing}
          onClose={() => setDialog(null)}
          onDone={done('Finanzierung wieder geöffnet.')}
        />
      ) : null}
      {dialog === 'link' ? (
        <LinkDealDialog
          open
          financing={financing}
          onClose={() => setDialog(null)}
          onDone={done('Finanzierung mit dem Deal verknüpft.')}
        />
      ) : null}
      {dialog === 'reassign' ? (
        <ReassignDialog financingId={financing.id} open onClose={() => setDialog(null)} />
      ) : null}
      {dialog === 'signature' ? (
        <SignatureDialog financing={financing} open onClose={() => setDialog(null)} />
      ) : null}
      {dialog === 'task' ? (
        <CreateTaskDialog
          open
          onClose={() => setDialog(null)}
          onCreated={() => {
            setDialog(null);
            onChanged();
            toast({ message: 'Task angelegt.' });
          }}
        />
      ) : null}
    </>
  );
}
